/* eslint-disable react/react-in-jsx-scope */
import { Button, Card, Checkbox, Col, Container, Flex, Icons, Row, Typography } from '@jcm/design-system';
import { useEffect, useState } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import CartaoNovidades from './CartaoNovidades';
import { Input } from 'antd';
import { TNovidade } from '../../../ts/types/Novidade';
import { CriarNovidadeZodFormValues, CriarNovidadeZodSchema } from '../../../zod/validations/CriarNovidadeZodSchema';
import { useGovernancaContext } from '../../../../../../context/GovernancaContext';
import ObrigacoesService from '../../../services/Obrigacoes/Obrigacoes.service';
import PopConfirmButton from '../../../../../../shared/components/popConfirmButton/popConfirmButton';
import styles from './index.module.scss';
import { EnumNovidade } from '../../../ts/enums/NovidadesEnum';
import { iconeNotificacao } from '../../../components/NotificacoesUsuario/RecuperarIconeNotificacao/recuperarIconeNotificacao';
import { formatarDataPtBr } from '../../../utils/datas/formatarDataPtBr';
import Lottie from 'lottie-react';
import CustomAlert from '../../../../../../shared/components/customAlert/customAlert';
import LoadingListaObrigacoes from '../../../../../../assets/loadings/CarregandoListaObrigacoes.json';

const { TextArea } = Input;

export default function Novidades(): JSX.Element {
	const [novidades, setNovidades] = useState<TNovidade[]>([]);
	const [criandoNotificacao, setCriandoNotificacao] = useState<boolean>(false);
	const [carregando, setCarregando] = useState<boolean>(true);
	const [alert, setAlert] = useState<{ type: 'success' | 'error'; content: string; title?: string } | null>(null);
	const [contadorCaractere, setContadorCaractere] = useState(0);
	const maximoCaracteresDescricao = 300;

	const { empresaAtiva, carregarNotificacoes } = useGovernancaContext();
	const _novidadesService = new ObrigacoesService();

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<CriarNovidadeZodFormValues>({
		resolver: zodResolver(CriarNovidadeZodSchema),
		defaultValues: {
			tipoNovidade: 13,
			descricao: '',
		},
	});
	const formValues = useWatch({ control });

	useEffect(() => {
		async function initComponent() {
			await buscarNovidades();
		}
		initComponent();
	}, []);

	async function buscarNovidades(): Promise<void> {
		if (empresaAtiva !== null) {
			const notificacoesResponse = await _novidadesService.getAllNovidades(empresaAtiva);
			setNovidades(notificacoesResponse.data.result);
			setCarregando(false);
		}
	}

	const inserirTamanhoCaracteres = (value: string) => {
		setContadorCaractere(value.length);
	};

	const onSubmit = async (data: CriarNovidadeZodFormValues) => {
		setCarregando(true);
		setCriandoNotificacao(true);
		try {
			await _novidadesService.criarNovidade(empresaAtiva ?? '', {
				...data,
				novidadeId: 0,
				dataInserida: new Date().toISOString(),
			});
			await buscarNovidades();

			setAlert({
				type: 'success',
				content: 'Notificação criada com sucesso.',
				title: 'Sucesso!',
			});
			carregarNotificacoes(empresaAtiva as unknown as number);

			reset();
		} catch (error) {
			if (error instanceof Error) {
				setAlert({
					type: 'error',
					content: `Erro ao criar notificação: ${error.message}`,
					title: 'Erro!',
				});
			} else {
				setAlert({
					type: 'error',
					content: 'Erro ao criar notificação: Erro desconhecido',
					title: 'Erro!',
				});
			}
		} finally {
			setCriandoNotificacao(false);
			setCarregando(false);
		}
	};

	const handleDelete = (index: number) => {
		const onConfirm = async () => {
			try {
				await _novidadesService.deletarNovidade(empresaAtiva ?? '', index);
				setNovidades((prev: TNovidade[]) => prev.filter((novidade) => novidade.novidadeId !== index));
				setAlert({ type: 'success', content: 'Notificação excluída com sucesso!', title: 'Sucesso' });
			} catch (error) {
				const err = error as Error;
				setAlert({
					type: 'error',
					content: `Erro ao criar notificação: ${err.message || 'Erro desconhecido'}`,
					title: 'Erro!',
				});
			}
		};

		return (
			<PopConfirmButton
				onConfirm={onConfirm}
				title='Deseja excluir essa notificação? Ela será removida totalmente do sistema.'
				okText='Sim, excluir!'
				cancelText='Cancelar'
				buttonText={<Icons.Delete className={styles.deleteIcon} />}
			/>
		);
	};

	const opcoesNotificacoes = [
		{ label: 'Notificação', value: EnumNovidade.Sistema },
		{ label: 'Anúncio', value: EnumNovidade.Informativo },
		{ label: 'Inconsistências no sistema', value: EnumNovidade.Bug },
	];

	return (
		<>
			<Row gutter={[16, 16]}>
				<Col xs={24}>
					<Flex justify='space-between'>
						<Typography.Title size='large'>Central de Notificações</Typography.Title>
						{!criandoNotificacao && (
							<Button type='filled-tonal' variant='default' onClick={() => setCriandoNotificacao((prev) => !prev)}>
								Nova notificação
							</Button>
						)}
					</Flex>
				</Col>

				{criandoNotificacao && (
					<Col xs={24}>
						<Container elevation='lowest' level={1} variant='default'>
							<form onSubmit={handleSubmit(onSubmit)}>
								<Row gutter={[18, 18]}>
									<Col lg={12}>
										<Row gutter={[16, 16]}>
											<Col lg={24}>
												<Controller
													name='tipoNovidade'
													control={control}
													rules={{ required: 'Selecione um tipo' }}
													render={({ field }) => (
														<Flex gap={8} vertical>
															<Typography.Body>
																Tipo <span className={styles.required}></span>
															</Typography.Body>
															<Flex gap={16} align='center'>
																{opcoesNotificacoes.map((opcao) => (
																	<Flex gap={8} key={opcao.value}>
																		<Checkbox
																			value={opcao.value}
																			checked={field.value === opcao.value}
																			onChange={() => field.onChange(opcao.value)}
																		/>
																		<label>{opcao.label}</label>
																	</Flex>
																))}
															</Flex>
															{errors.tipoNovidade && (
																<Typography.Label variant='error'>
																	{errors.tipoNovidade.message as string}
																</Typography.Label>
															)}
														</Flex>
													)}
												/>
											</Col>

											<Col lg={24}>
												<Controller
													name='descricao'
													control={control}
													render={({ field }) => (
														<Flex vertical gap={8}>
															<Typography.Body>
																Descrição <span className={styles.required}>*</span>
															</Typography.Body>
															<TextArea
																allowClear
																variant='filled'
																placeholder='Insira a descrição da notificação'
																{...field}
																onChange={(e) => {
																	inserirTamanhoCaracteres(e.target.value);
																	field.onChange(e);
																}}
																className={styles.textArea}
																maxLength={300}
															/>
															<Flex justify='space-between' align='center'>
																{errors.descricao && (
																	<Typography.Label variant='error'>
																		{errors.descricao.message as string}
																	</Typography.Label>
																)}
																<Typography.Label
																	className={
																		contadorCaractere > maximoCaracteresDescricao - 30 ? styles.alertaCaractere : ''
																	}
																>
																	{contadorCaractere}/{maximoCaracteresDescricao}
																</Typography.Label>
															</Flex>
														</Flex>
													)}
												/>
											</Col>
										</Row>
									</Col>

									<Col lg={12}>
										<Flex vertical gap={8}>
											<Typography.Body>Pré-visualização</Typography.Body>
											<Card level={1} variant='default' elevation='lowest'>
												<Row align='middle' gutter={[10, 8]}>
													<Col flex='auto' lg={24}>
														<Flex justify='space-between' align='center'>
															<Row>
																<Flex gap={8} align='center'>
																	{formValues.tipoNovidade && iconeNotificacao(formValues.tipoNovidade)}
																</Flex>
															</Row>
														</Flex>
													</Col>

													<Col lg={24} className={styles.containerDescricao}>
														<Typography.Body size='medium'>{formValues.descricao}</Typography.Body>
													</Col>

													<Col lg={24}>
														<Flex justify='space-between' align='center'>
															<Typography.Label className={styles.dateLabel}>
																{formatarDataPtBr(new Date().toISOString())}
															</Typography.Label>
														</Flex>
													</Col>
												</Row>
											</Card>
										</Flex>
									</Col>

									<Col lg={24}>
										<Flex justify='center' align='center' gap={12}>
											<Button
												type='outlined'
												variant='error'
												onClick={() => setCriandoNotificacao(false)}
												htmlType='button'
											>
												Cancelar
											</Button>
											<Button type='filled-tonal' htmlType='submit' loading={carregando}>
												Publicar
											</Button>
										</Flex>
									</Col>
								</Row>
							</form>
						</Container>
					</Col>
				)}

				<Col xs={24}>
					{carregando ? (
						<div className={styles.loadingContainer}>
							<Lottie loop animationData={LoadingListaObrigacoes} className={styles.lottie} />
						</div>
					) : (
						<>
							{novidades.length > 0 ? (
								<Row gutter={[16, 16]}>
									{novidades.map((item, index) => (
										<Col span={8} key={index}>
											<CartaoNovidades
												descricao={item.descricao}
												tipoNovidade={item.tipoNovidade}
												onDelete={handleDelete}
												indice={item.novidadeId}
												dataInserida={item.dataInserida}
											/>
										</Col>
									))}
								</Row>
							) : (
								<div className={styles.emptyContainer}>
									<Flex justify='center'>
										<Typography.Headline>Não há novidades a serem exibidas.</Typography.Headline>
									</Flex>
								</div>
							)}
						</>
					)}
				</Col>
			</Row>
			{alert && <CustomAlert type={alert.type} content={alert.content} title={alert.title} />}
		</>
	);
}
